import { Box, Divider, Typography } from '@material-ui/core';
import React, { FunctionComponent } from 'react';
import LabelAndPrice from '../labels/LabelAndPrice';
import LabelAndText from '../labels/LabelAndText';
import { IFinanceQuote } from '../../../../../../common/model/IFinanceQuote';
import LabelAndPercentage from '../labels/LabelAndPercentage';
import FinanceCalculatorContainer from '../../../financeCalculator/FinanceCalculatorContainer';
import { ICarDetails } from '../../../../../../../../common/model/ICarDetails';
import IndicativeNumber from '../../../../../../common/components/indicativeNumber/IndicativeNumber';
import useScrollToSection from '../../../../../react/navigation/useScrollToSection';
import useTheme from '@material-ui/core/styles/useTheme';
import { ISpcTheme } from '../../../../../../common/theme/ISpcTheme';
import usePlatformConfig from '../../../../../../common/react/usePlatformConfig';
import useFinancePackageFrequencyLabelProvider from '../../../../../../common/react/useFinancePackageFrequencyLabelProvider';
import Translation from '../../../../../../common/components/translation/Translation';

interface ISummaryFinanceMinimalProps {
  loading?: boolean;
  currency: string;
  monthlyPayment: number;
  shouldRenderRates: boolean;
  shouldRenderTerm: boolean;
  financeQuote: IFinanceQuote;
  carConfiguration: ICarDetails;
  toggleFinanceCalculatorSpc?: (shouldOpen: boolean) => void;
}

const SummaryFinanceMinimal: FunctionComponent<ISummaryFinanceMinimalProps> = ({
  currency,
  loading,
  shouldRenderRates,
  shouldRenderTerm,
  financeQuote,
  carConfiguration,
  toggleFinanceCalculatorSpc,
  monthlyPayment,
}) => {
  const financePackageFrequencyLabelProvider = useFinancePackageFrequencyLabelProvider();
  const { scrollToDisclaimer } = useScrollToSection();
  const {
    spc: {
      customise: { displaySummaryTerm, displayFinanceDeposit },
    },
  } = useTheme<ISpcTheme>();
  const {
    platformConfig: {
      priceDisplay: { finance },
    },
  } = usePlatformConfig();

  const renderRepaymentLabel = () => {
    return (
      <Box display="flex" data-test="customise:summary:repayment" component="div">
        <Typography>
          {shouldRenderRates ? (
            financePackageFrequencyLabelProvider(financeQuote.packageCode, 'long')
          ) : (
            <Translation id={'spc.customise.repayment'} />
          )}
          <IndicativeNumber
            dataTest={'repayment'}
            onClick={scrollToDisclaimer}
            displayNumber={<Translation id={'spc.customise.indicative.repayment'} />}
          />
        </Typography>
      </Box>
    );
  };

  const renderDepositLabel = () => {
    return (
      <Box display="flex" data-test="customise:summary:deposit" component="span">
        <Translation id={'spc.customise.deposit'} />
      </Box>
    );
  };

  const renderComparisonLabel = () => {
    return (
      <Box display="flex" data-test="configurator:summary:comparisonRate" component="div">
        <Typography>
          <Translation id={'spc.customise.footer.finance.comparisonRate'} />
          <IndicativeNumber
            dataTest={'comparisonRate'}
            onClick={scrollToDisclaimer}
            displayNumber={<Translation id={'spc.customise.indicative.comparisonRate'} />}
          />
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {!financeQuote.isCashProduct && (
        <>
          <Divider />
          <LabelAndPrice
            loading={loading}
            decimals={finance?.decimalScale}
            label={renderRepaymentLabel()}
            price={monthlyPayment}
            priceType="repayment_frequency"
            currency={currency}
          />
        </>
      )}
      {displayFinanceDeposit && !financeQuote.isCashProduct && (
        <>
          <LabelAndPrice
            loading={loading}
            decimals={finance?.decimalScale}
            label={renderDepositLabel()}
            price={financeQuote.deposit}
            priceType="deposit"
            currency={currency}
          />
        </>
      )}
      {displaySummaryTerm && shouldRenderTerm && (
        <LabelAndText
          label={<Translation id={'spc.customise.footer.finance.termRate'} />}
          dataTest="summary:termRate"
          text={<Translation id={'spc.customise.durationLabel'} values={{ duration: +financeQuote.duration }} />}
        />
      )}
      {shouldRenderRates && (
        <LabelAndPercentage
          label={renderComparisonLabel()}
          percentage={financeQuote.comparisonRate}
          percentageType={'comparisonRate'}
          loading={loading}
        />
      )}
      {carConfiguration && (
        <FinanceCalculatorContainer
          externalId={carConfiguration?.externalId}
          carConfiguration={carConfiguration}
          isModalOpen={false}
          toggleModal={toggleFinanceCalculatorSpc}
        />
      )}
    </>
  );
};

export default SummaryFinanceMinimal;
